import React from "react";
import { motion, useReducedMotion } from "motion/react";
import { Link } from "react-router-dom";
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  YoutubeIcon,
} from "lucide-react";

const footerLinks = [
  {
    label: "Navigation",
    links: [
      { title: "Accueil", href: "/#top" },
      { title: "Services", href: "/#services" },
      { title: "Travaux", href: "/#projects" },
      { title: "Contact", href: "/#contact" },
    ],
  },
  {
    label: "Entreprise",
    links: [
      { title: "Pourquoi moi", href: "/#why-us" },
      { title: "À propos", href: "/about" },
      { title: "Avis clients", href: "/#testimonials" },
    ],
  },
  {
    label: "Ressources",
    links: [
      { title: "Aide", href: "/help" },
      { title: "FAQ", href: "/faqs" },
      { title: "Blog", href: "/blog" },
    ],
  },
  {
    label: "Réseaux",
    links: [
      { title: "Facebook", href: "#", icon: FacebookIcon },
      { title: "Instagram", href: "https://www.instagram.com/xaimrane", icon: InstagramIcon },
      { title: "Youtube", href: "#", icon: YoutubeIcon },
      { title: "LinkedIn", href: "#", icon: LinkedinIcon },
    ],
  },
];

export function Footer() {
  return (
    <footer className="relative border-t border-white/[0.06] bg-black">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand/30 to-transparent" />

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:py-20">
        <div className="mb-10 flex flex-col gap-6 border-b border-white/[0.06] pb-10 md:mb-12 md:flex-row md:items-end md:justify-between md:pb-12">
          <div>
            <p className="font-display text-2xl font-bold text-white md:text-3xl">
              aimrane<span className="text-brand">.</span>
              <span className="text-zinc-500">digital</span>
            </p>
            <p className="mt-2 max-w-md text-sm text-zinc-500">
              Freelance web — design, développement et visibilité pour des sites qui performent.
            </p>
          </div>
          <Link
            to="/#contact"
            className="inline-flex w-full items-center justify-center rounded-xl border border-brand/30 bg-brand/10 px-4 py-2.5 text-sm font-medium text-brand transition hover:border-brand/50 hover:bg-brand/15 hover:text-brand-bright md:w-auto md:justify-start md:border-0 md:bg-transparent md:p-0"
          >
            Formulaire de contact →
          </Link>
        </div>

        <div className="grid w-full grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {footerLinks.map((section, index) => (
            <AnimatedContainer key={section.label} delay={0.05 + index * 0.05}>
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
                {section.label}
              </h3>
              <ul className="mt-3 space-y-1.5 text-sm text-zinc-400">
                {section.links.map((link) => (
                  <li key={link.title}>
                    {link.href.startsWith("/") ? (
                      <Link
                        to={link.href}
                        className="inline-flex min-h-8 items-center transition hover:text-brand"
                      >
                        {link.icon && <link.icon className="me-2 size-4 opacity-70" />}
                        {link.title}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="inline-flex min-h-8 items-center transition hover:text-brand"
                        target={link.href.startsWith("http") ? "_blank" : undefined}
                        rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      >
                        {link.icon && <link.icon className="me-2 size-4 opacity-70" />}
                        {link.title}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </AnimatedContainer>
          ))}
        </div>

        <p className="mt-14 text-center text-xs text-zinc-600 md:text-left">
          © {new Date().getFullYear()} Aimrane Digital. Tous droits réservés.
        </p>
      </div>
    </footer>
  );
}

function AnimatedContainer({ className, delay = 0.1, children }) {
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.45 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
